import { ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import classNames from 'classnames';

import { registrationNotCompletedSelector } from '@lootboy/lib/selectors';

import { MENU_DIALOG_CONTAINER_ID } from '~/config';
import { useClosePopup } from '~/hooks/useClosePopup';
import { previousPathSelector } from '~/redux/slices/location/selectors';
import { TAppStore } from '~/redux/store/useAppDispatch';
import menuConsts from '~/routes/MenuRouter/menuRouter.consts.module.scss';
import {
  getMenuViewTransitionDirection,
  menuRoutes,
} from '~/routes/MenuRouter/menuRoutes';

import styles from './MenuDialogContainer.module.scss';

type TMenuDialogContainerProps = {
  onOutsideClick?: () => void;
  fixedHeight?: boolean;
  children: ReactNode;
};

export function MenuDialogContainer({
  onOutsideClick,
  fixedHeight = false,
  children,
}: TMenuDialogContainerProps): JSX.Element {
  const { pathname } = useLocation();
  const from = useSelector(previousPathSelector);
  const closePopup = useClosePopup();

  const registrationNotCompleted = useSelector<TAppStore, boolean>(
    registrationNotCompletedSelector,
  );

  const transitionDirection = getMenuViewTransitionDirection(pathname, from);

  return ReactDOM.createPortal(
    <div
      className={styles.dialogBackground}
      onClick={({ currentTarget, target }) => {
        if (currentTarget.isEqualNode(target as Node)) {
          if (onOutsideClick) {
            onOutsideClick();
          } else if (
            !registrationNotCompleted ||
            !menuRoutes[pathname].preventOverlayClose
          ) {
            closePopup();
          }
        }
      }}
    >
      <TransitionGroup
        className={classNames(
          styles.dialogElement,
          styles[transitionDirection],
          fixedHeight && styles.fixedHeight,
        )}
        id={MENU_DIALOG_CONTAINER_ID}
      >
        <CSSTransition
          classNames={menuConsts.menuViewsClass}
          timeout={Number(menuConsts.menuTransitionDuration)}
          // don't trigger a transition if the dialog is being used outside the menu router (e.g. GDPRDialog)
          key={onOutsideClick ? undefined : pathname}
        >
          <main className={styles.mainContainer}>{children}</main>
        </CSSTransition>
      </TransitionGroup>
    </div>,
    document.body,
  );
}
